import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import InvoicePrintView from '../components/InvoicePrintView';
import ProductCombobox from '../components/ProductCombobox';
import { openPdf } from '../utils/pdfHelpers';
import {
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PrinterIcon,
  DocumentDuplicateIcon,
  DocumentArrowDownIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Tip } from '@/components/ui/tip';
import { DatePicker } from '../components/ui/date-picker';
import MultiRemitoInvoiceDialog from '../components/MultiRemitoInvoiceDialog';
import { confirmDialog } from '../components/ConfirmDialog';

interface InvoiceItem {
  id?: string;
  productId?: string;
  sku?: string;
  isService?: boolean;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  type: 'A' | 'B' | 'C';
  clientId: string;
  budgetId?: string;
  issueDate: string;
  dueDate?: string;
  clientName: string;
  clientDocument?: string;
  clientTaxCondition: string;
  clientAddress?: string;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  discount: number;
  items: InvoiceItem[];
  notes?: string;
  status: 'draft' | 'authorized' | 'sent' | 'paid' | 'cancelled';
  afipCae?: string;
  afipCaeVto?: string;
  afipCbteTipo?: number;
  afipCbteNro?: number;
  afipPtoVta?: number;
  afipAuthorizedAt?: string;
  paymentMethod?: string;
  createdAt: string;
  remitoId?: string;
  relatedRemitoIds?: string;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  cuit?: string;
  dni?: string;
  email: string;
  address?: string;
  type: 'individual' | 'company';
}

interface RemitoForInvoice {
  id: string;
  remitNumber: string;
  clientId: string;
  clientName: string;
  estimatedValue: number;
  status: string;
  items: any[];
}

interface InvoiceProduct {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  category: string;
  salePrice?: number;
  stock?: number;
  isService?: boolean;
  sellingPrice: number;
}

const Invoices: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [remitosForInvoice, setRemitosForInvoice] = useState<RemitoForInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFromRemitoModal, setShowFromRemitoModal] = useState(false);
  const [showMultiRemitoDialog, setShowMultiRemitoDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [products, setProducts] = useState<InvoiceProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedRemito, setSelectedRemito] = useState<string>('');
  const [sendingWhatsAppId, setSendingWhatsAppId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterTaxCondition, setFilterTaxCondition] = useState<string>('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<string>('');
  const [paymentMethods, setPaymentMethods] = useState<{id: string; name: string; code: string}[]>([]);

  // Etiqueta legible del medio de pago (usa el nombre de la tabla; si no, mapea el codigo)
  const paymentLabel = (code?: string) => {
    if (!code) return '-';
    if (code === 'cuenta_corriente') return 'Cta. Cte.';
    const pm = paymentMethods.find((p) => p.code === code);
    if (pm?.name) return pm.name;
    const map: Record<string, string> = {
      efectivo: 'Efectivo',
      cuenta_corriente: 'Cta. Cte.',
      transferencia: 'Transferencia',
      tarjeta_debito: 'Débito',
      tarjeta_credito: 'Crédito',
      debito: 'Débito',
      credito: 'Crédito',
      cheque: 'Cheque',
      qr: 'QR',
      cripto: 'Cripto',
      mixto: 'Mixto',
    };
    return map[code] || code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  const watchClientId = watch('clientId');
  const watchType = watch('type');

  const fetchPaymentMethods = async () => {
    try {
      const res = await axios.get('/api/settings/payment-methods');
      setPaymentMethods(res.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  // Fetch data
  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchRemitosForInvoice();
    fetchProducts();
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== searchTerm) {
      setSearchTerm(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key.toLowerCase() === 'n') {
        event.preventDefault();
        setShowCreateModal(true);
      }
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/invoices?limit=500&page=1');
      setInvoices(response.data.invoices);
    } catch (error) {
      toast.error('Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    let result = invoices;
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter((invoice) => {
        const haystack = [
          invoice.invoiceNumber,
          invoice.clientName,
          invoice.clientDocument
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(term);
      });
    }
    if (filterType) {
      result = result.filter((inv) => inv.type === filterType);
    }
    if (filterDateFrom) {
      result = result.filter((inv) => inv.issueDate >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter((inv) => inv.issueDate <= filterDateTo + 'T23:59:59');
    }
    if (filterTaxCondition) {
      result = result.filter((inv) => inv.clientTaxCondition === filterTaxCondition);
    }
    if (filterPaymentMethod) {
      result = result.filter((inv) => inv.paymentMethod === filterPaymentMethod);
    }
    return result;
  }, [invoices, searchTerm, filterType, filterDateFrom, filterDateTo, filterTaxCondition, filterPaymentMethod]);

  // Paginación cliente
  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / PAGE_SIZE));
  const pagedInvoices = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredInvoices.slice(start, start + PAGE_SIZE);
  }, [filteredInvoices, currentPage, PAGE_SIZE]);

  // Reset page cuando cambian los filtros
  const prevFiltersRef = useRef({ searchTerm, filterType, filterDateFrom, filterDateTo, filterTaxCondition, filterPaymentMethod });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (prev.searchTerm !== searchTerm || prev.filterType !== filterType || prev.filterDateFrom !== filterDateFrom || prev.filterDateTo !== filterDateTo) {
      setCurrentPage(1);
      prevFiltersRef.current = { searchTerm, filterType, filterDateFrom, filterDateTo, filterTaxCondition, filterPaymentMethod };
    }
  }, [searchTerm, filterType, filterDateFrom, filterDateTo, filterTaxCondition, filterPaymentMethod]);

  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients?limit=1000');
      setClients(response.data.clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchRemitosForInvoice = async () => {
    try {
      const response = await axios.get('/api/remitos?status=issued');
      const remitos = response.data.remitos || [];
      setRemitosForInvoice(remitos.filter((r: any) => !r.invoicedAt));
    } catch (error) {
      console.error('Error fetching remitos:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await axios.get('/api/products?limit=all');
      const list = response.data.products || [];
      const mapped = list.map((product: InvoiceProduct) => ({
        ...product,
        sellingPrice: product.salePrice ?? product.sellingPrice ?? 0,
        isService: product.category === 'servicio'
      }));
      setProducts(mapped);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };


  // Auto-determine invoice type based on client
  useEffect(() => {
    if (watchClientId) {
      const client = clients.find(c => c.id === watchClientId);
      if (client) {
        if (client.cuit && validateCUIT(client.cuit)) {
          setValue('type', 'A');
        } else {
          setValue('type', 'B');
        }
        setValue('clientName', client.companyName || `${client.firstName} ${client.lastName}`);
        setValue('clientDocument', client.cuit || client.dni);
        setValue('clientAddress', client.address);
      }
    }
  }, [watchClientId, clients, setValue]);

  // CUIT validation
  const validateCUIT = (cuit: string): boolean => {
    if (!cuit) return false;
    const cleanCUIT = cuit.replace(/[-\s]/g, '');
    if (cleanCUIT.length !== 11 || !/^\d+$/.test(cleanCUIT)) return false;
    
    const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCUIT[i]) * multipliers[i];
    }
    const remainder = sum % 11;
    const checkDigit = remainder < 2 ? remainder : 11 - remainder;
    return parseInt(cleanCUIT[10]) === checkDigit;
  };

  // Format CUIT
  const formatCUIT = (cuit: string): string => {
    const clean = cuit.replace(/[-\s]/g, '');
    if (clean.length !== 11) return cuit;
    return `${clean.substring(0, 2)}-${clean.substring(2, 10)}-${clean.substring(10)}`;
  };

  // Calculate totals - precios son finales (IVA incluido)
  // Se extrae el IVA por item, no se suma
  const calculateTotals = (items: InvoiceItem[], taxRate: number = 21) => {
    let totalFinal = 0;
    let totalNeto = 0;
    items.forEach(item => {
      const itemTotal = item.quantity * item.unitPrice;
      const itemNeto = parseFloat((itemTotal / (1 + taxRate / 100)).toFixed(2));
      totalFinal += itemTotal;
      totalNeto += itemNeto;
    });
    totalFinal = parseFloat(totalFinal.toFixed(2));
    totalNeto = parseFloat(totalNeto.toFixed(2));
    const tax = parseFloat((totalFinal - totalNeto).toFixed(2));
    return { subtotal: totalNeto, tax, total: totalFinal };
  };

  // Add item
  const addItem = () => {
    const newItem: InvoiceItem = {
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    };
    setItems([...items, newItem]);
  };

  // Remove item
  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // Update item
  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Calculate total for this item
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index].total = updatedItems[index].quantity * updatedItems[index].unitPrice;
    }
    
    setItems(updatedItems);
  };

  const handleSelectProduct = (index: number, product: InvoiceProduct) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      productId: product.id,
      sku: product.sku,
      isService: product.isService,
      description: product.name,
      unitPrice: Number(product.sellingPrice || 0),
      total: (updatedItems[index].quantity || 0) * Number(product.sellingPrice || 0)
    };
    setItems(updatedItems);
  };

  // Create invoice
  const onSubmit = async (data: any) => {
    try {
      if (items.length === 0) {
        toast.error('Debe agregar al menos un item');
        return;
      }

      const invoiceData = {
        ...data,
        items: items,
        taxRate: 21
      };

      await axios.post('/api/invoices', invoiceData);
      toast.success('Factura creada exitosamente');
      setShowCreateModal(false);
      reset();
      setItems([]);
      fetchInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear la factura');
    }
  };

  // Create invoice from remito
  const createFromRemito = async () => {
    if (!selectedRemito) {
      toast.error('Debe seleccionar un remito');
      return;
    }

    try {
      await axios.post(`/api/invoices/from-remito/${selectedRemito}`);
      toast.success('Factura creada desde remito exitosamente');
      setShowFromRemitoModal(false);
      setSelectedRemito('');
      fetchInvoices();
      fetchRemitosForInvoice();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al crear la factura');
    }
  };

  // Delete invoice
  const deleteInvoice = async (invoiceId: string) => {
    if (!await confirmDialog('¿Está seguro de eliminar esta factura?')) return;

    try {
      await axios.delete(`/api/invoices/${invoiceId}`);
      toast.success('Factura eliminada exitosamente');
      fetchInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al eliminar la factura');
    }
  };

  const authorizeInAfip = async (invoiceId: string) => {
    // No autorizar sin medio de pago cargado (misma regla que valida el backend).
    const inv = invoices.find((i) => i.id === invoiceId);
    if (inv && !String(inv.paymentMethod || '').trim()) {
      toast.error('Cargá el medio de pago antes de autorizar la factura.');
      return;
    }
    if (!await confirmDialog('¿Enviar esta factura a AFIP para autorizarla?')) return;
    try {
      toast.message('Enviando a AFIP...');
      const response = await axios.post(`/api/invoices/${invoiceId}/authorize-afip`);
      const cae = response.data?.afip?.cae;
      toast.success(cae ? `Factura autorizada. CAE ${cae}` : 'Factura autorizada');
      await fetchInvoices();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Error al autorizar en AFIP');
    }
  };

  // Print invoice
  const printInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPrintView(true);
  };

  // Download PDF
  const handleDownloadPdf = async (invoice: Invoice) => {
    try {
      const res = await axios.post(`/api/invoices/${invoice.id}/generate-pdf`, {}, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const pv = String(invoice.afipPtoVta || 2).padStart(4, '0');
      const nro = String(invoice.afipCbteNro || 0).padStart(8, '0');
      const filename = invoice.afipCbteNro
        ? `Factura-${invoice.type}-${pv}-${nro}.pdf`
        : `Factura-${invoice.invoiceNumber}.pdf`;
      const result = openPdf({ blob, filename });
      toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abierto');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al generar PDF');
    }
  };

  // Envía la factura por WhatsApp usando el template de Kapso con el PDF adjunto
  // (endpoint del server). No abre wa.me: el server manda el comprobante solo.
  const handleSendWhatsApp = async (invoice: Invoice) => {
    if (sendingWhatsAppId) return;
    setSendingWhatsAppId(invoice.id);
    const toastId = toast.loading('Enviando factura por WhatsApp...');
    try {
      const res = await axios.post(`/api/invoices/${invoice.id}/send-whatsapp`);
      toast.success(res.data?.message || 'Factura enviada por WhatsApp', { id: toastId });
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.response?.data?.details || 'No se pudo enviar la factura por WhatsApp', { id: toastId });
    } finally {
      setSendingWhatsAppId(null);
    }
  };

  const handleSendEmail = (invoice: Invoice) => {
    const client = clients.find(c => c.id === invoice.clientId);
    const email = client?.email || '';
    const pv = String(invoice.afipPtoVta || 2).padStart(4, '0');
    const nro = String(invoice.afipCbteNro || invoice.invoiceNumber).padStart(8, '0');
    const subject = encodeURIComponent(`Factura ${invoice.type} ${pv}-${nro}`);
    const body = encodeURIComponent(
      `Estimado/a ${invoice.clientName},\n\nAdjuntamos la Factura ${invoice.type} ${pv}-${nro} por un total de $${invoice.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}.\nCAE: ${invoice.afipCae || 'Pendiente'}.\n\nSaludos.`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  const getStatusColor = (status: string) => statusClass('invoice', status);
  const getStatusText = (status: string) => statusLabel('invoice', status);

  // La NC no borra la factura, pero si la cubre entera el comprobante ya no
  // vale: la lista tiene que decirlo, si no parece vigente.
  const badgeEstado = (inv: any) => {
    if (inv?.anulada) return { texto: 'Anulada por NC', clase: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
    if (inv?.parcial) return { texto: 'NC parcial', clase: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' };
    return { texto: getStatusText(inv?.status), clase: getStatusColor(inv?.status) };
  };

  const getTypeColor = (type: string) => {
    const colors = {
      A: 'bg-indigo-100 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-300',
      B: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300',
      C: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
    };
    return colors[type as keyof typeof colors] || colors.B;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Facturas</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestión de facturas electrónicas</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Tip label="Facturar varios remitos en cuenta corriente del mismo cliente en una sola factura"><button
            onClick={() => setShowMultiRemitoDialog(true)}
            className="px-4 py-2 border border-cyan-300 dark:border-cyan-700 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 text-sm text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors flex items-center"
          >
            <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
            Multi-remito (cta cte)
          </button></Tip>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nueva Factura
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por número o cliente..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
          />
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="shrink-0 px-3 py-2 pr-10 min-w-[190px] border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
        >
          <option value="">Tipo: Todos</option>
          <option value="A">Factura A</option>
          <option value="B">Factura B</option>
        </select>
        <Tip label="Fecha desde"><span className="inline-flex shrink-0"><DatePicker
          value={filterDateFrom}
          onChange={setFilterDateFrom}
          className="px-3 py-2 min-w-[170px] border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
          placeholder="Desde"
        /></span></Tip>
        <Tip label="Fecha hasta"><span className="inline-flex shrink-0"><DatePicker
          value={filterDateTo}
          onChange={setFilterDateTo}
          className="px-3 py-2 min-w-[170px] border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
          placeholder="Hasta"
        /></span></Tip>
        <select
          value={filterTaxCondition}
          onChange={(e) => setFilterTaxCondition(e.target.value)}
          className="shrink-0 px-3 py-2 pr-10 min-w-[210px] border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
        >
          <option value="">Cond. IVA: Todas</option>
          <option value="RESPONSABLE_INSCRIPTO">Resp. Inscripto</option>
          <option value="MONOTRIBUTO">Monotributo</option>
          <option value="EXENTO">Exento</option>
          <option value="CONSUMIDOR_FINAL">Consumidor Final</option>
        </select>
        <select
          value={filterPaymentMethod}
          onChange={(e) => setFilterPaymentMethod(e.target.value)}
          className="shrink-0 px-3 py-2 pr-10 min-w-[220px] border border-gray-200 dark:border-gray-800 rounded-xl bg-background text-sm"
        >
          <option value="">Medio de pago: Todos</option>
          {paymentMethods.map((pm) => (
            <option key={pm.id} value={pm.name}>{pm.name}</option>
          ))}
        </select>
        {(filterType || filterDateFrom || filterDateTo || filterTaxCondition || filterPaymentMethod) && (
          <button
            onClick={() => { setFilterType(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterTaxCondition(''); setFilterPaymentMethod(''); }}
            className="shrink-0 px-3 py-2 text-sm text-muted-foreground hover:text-foreground whitespace-nowrap"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Invoices List */}
      <div className="bg-background rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {/* Teléfono: tarjetas (la tabla de 8 columnas obligaba a scrollear de costado) */}
        <div className="md:hidden divide-y divide-border">
          {pagedInvoices.map((invoice) => (
            <div key={invoice.id} className="p-3 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{invoice.invoiceNumber}</span>
                <Badge variant="secondary" className={getTypeColor(invoice.type)}>
                  Factura {invoice.type}
                </Badge>
                <Badge variant="secondary" className={badgeEstado(invoice).clase}>
                  {badgeEstado(invoice).texto}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(invoice.issueDate).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                </span>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{invoice.clientName}</div>
                {invoice.clientDocument && (
                  <div className="text-xs text-muted-foreground">{formatCUIT(invoice.clientDocument)}</div>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{paymentLabel(invoice.paymentMethod)}</span>
                <span className="font-semibold">
                  ${invoice.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  {invoice.taxAmount > 0 && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({invoice.type === 'B' ? 'IVA cont.' : 'IVA'} ${invoice.taxAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button variant="outline" size="sm" className="h-9" onClick={() => { setSelectedInvoice(invoice); setShowViewModal(true); }}>
                  <EyeIcon className="h-4 w-4 mr-1.5" />Ver
                </Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => handleDownloadPdf(invoice)}>
                  <PrinterIcon className="h-4 w-4 mr-1.5" />PDF
                </Button>
                {invoice.status === 'draft' && (
                  <Button size="sm" className="h-9" onClick={() => authorizeInAfip(invoice.id)}>
                    <CheckIcon className="h-4 w-4 mr-1.5" />Autorizar
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-1.5">
                  {invoice.status === 'authorized' && (
                    <>
                      <Button variant="outline" size="icon" className="size-9" aria-label="WhatsApp" disabled={sendingWhatsAppId === invoice.id} onClick={() => handleSendWhatsApp(invoice)}>
                        <WhatsAppIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="size-9" aria-label="Email" onClick={() => handleSendEmail(invoice)}>
                        <EnvelopeIcon className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {invoice.status === 'authorized' && invoice.afipCae && (
                    <Button variant="outline" size="icon" className="size-9" aria-label="Nota de crédito" onClick={() => navigate(`/notas-credito/nueva/${invoice.id}`)}>
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </Button>
                  )}
                  {invoice.status === 'draft' && (
                    <Button variant="outline" size="icon" className="size-9 text-muted-foreground hover:text-red-500" aria-label="Eliminar" onClick={() => deleteInvoice(invoice.id)}>
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto cq-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-muted/50 dark:hover:bg-zinc-900 border-b">
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Número</TableHead>
                <TableHead className="cq-hide-narrow text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Total</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Medio</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                <TableHead className="text-foreground h-8 px-2 text-right align-middle font-medium whitespace-nowrap"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagedInvoices.map((invoice) => (
                <TableRow key={invoice.id} className="border-b hover:bg-muted/50 dark:hover:bg-zinc-900">
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                    <div className="font-medium">{invoice.invoiceNumber}</div>
                  </TableCell>
                  <TableCell className="cq-hide-narrow py-1 px-2 align-middle whitespace-nowrap text-left">
                    <Badge variant="secondary" className={getTypeColor(invoice.type)}>
                      <span className="2xl:hidden">{invoice.type}</span>
                      <span className="hidden 2xl:inline">Factura {invoice.type}</span>
                    </Badge>
                  </TableCell>
                  <TableCell className="cq-clamp-narrow cq-clamp-wide py-1 px-2 align-middle text-left max-w-[200px]">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="font-medium truncate cursor-default">{invoice.clientName}</div>
                      </TooltipTrigger>
                      <TooltipContent side="top">{invoice.clientName}</TooltipContent>
                    </Tooltip>
                    {invoice.clientDocument && (
                      <div className="text-xs text-muted-foreground truncate">{formatCUIT(invoice.clientDocument)}</div>
                    )}
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                    {new Date(invoice.issueDate).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                    <div className="font-medium">${invoice.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
                    {invoice.taxAmount > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {invoice.type === 'B' ? 'IVA cont.' : 'IVA'}: ${invoice.taxAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                    <span className="text-sm">{paymentLabel(invoice.paymentMethod)}</span>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                    <Tip label={(invoice as any).creditNoteNumbers ? `Notas de credito: ${(invoice as any).creditNoteNumbers}` : badgeEstado(invoice).texto}>
                      <Badge variant="secondary" className={badgeEstado(invoice).clase}>
                        {badgeEstado(invoice).texto}
                      </Badge>
                    </Tip>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end">
                      <ButtonGroup>
                        <Tip label="Ver detalles"><Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowViewModal(true);
                          }}
                          className="size-8"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Button></Tip>
                        {(() => {
                          let ids: string[] = [];
                          if (invoice.remitoId) ids.push(invoice.remitoId);
                          try { ids = ids.concat(JSON.parse(invoice.relatedRemitoIds || '[]')); } catch { /* noop */ }
                          ids = Array.from(new Set(ids.filter(Boolean)));
                          if (ids.length === 0) return null;
                          return (
                            <Tip label={`Ver ${ids.length > 1 ? 'remitos' : 'remito'} en la sección Remitos`}><Button
                              variant="outline"
                              size="icon"
                              onClick={() => navigate(`/remitos?remitoIds=${ids.join(',')}`)}
                              className="size-8"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                            </Button></Tip>
                          );
                        })()}
                        <Tip label="Ver PDF"><Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleDownloadPdf(invoice)}
                          className="size-8"
                        >
                          <PrinterIcon className="h-4 w-4" />
                        </Button></Tip>
                        {invoice.status === 'authorized' && (
                          <>
                            <Tip label="Enviar por WhatsApp"><Button
                              variant="outline"
                              size="icon"
                              disabled={sendingWhatsAppId === invoice.id}
                              onClick={() => handleSendWhatsApp(invoice)}
                              className="size-8"
                            >
                              <WhatsAppIcon className="h-4 w-4" />
                            </Button></Tip>
                            <Tip label="Enviar por Email"><Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleSendEmail(invoice)}
                              className="size-8"
                            >
                              <EnvelopeIcon className="h-4 w-4" />
                            </Button></Tip>
                          </>
                        )}
                        {invoice.status === 'authorized' && invoice.afipCae && (
                          <Tip label="Crear Nota de Crédito"><Button
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/notas-credito/nueva/${invoice.id}`)}
                            className="size-8"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </Button></Tip>
                        )}
                        {invoice.status === 'draft' && (
                          <>
                            <Tip label="Autorizar en AFIP"><Button
                              variant="outline"
                              size="icon"
                              onClick={() => authorizeInAfip(invoice.id)}
                              className="size-8"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </Button></Tip>
                            <Tip label="Eliminar"><Button
                              variant="outline"
                              size="icon"
                              onClick={() => deleteInvoice(invoice.id)}
                              className="size-8"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button></Tip>
                          </>
                        )}
                      </ButtonGroup>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

          {filteredInvoices.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm.trim() ? 'No se encontraron resultados' : 'No hay facturas registradas'}
              </p>
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-border">
              <span className="text-sm text-muted-foreground">
                {filteredInvoices.length} facturas — página {currentPage} de {totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 rounded text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >«</button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >Anterior</button>
                {/* Números de página solo en pantallas con lugar */}
                <span className="hidden sm:flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '...'
                    ? <span key={`dots-${i}`} className="px-2 py-1 text-sm text-muted-foreground">…</span>
                    : <button key={p} onClick={() => setCurrentPage(p as number)} className={`px-3 py-1 rounded text-sm border ${currentPage === p ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}>{p}</button>
                  )}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >Siguiente</button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 rounded text-sm border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                >»</button>
              </div>
            </div>
          )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nueva Factura</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  reset();
                  setItems([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Client Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cliente *</label>
                  <select
                    {...register('clientId', { required: 'Cliente es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Seleccionar cliente...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.companyName || `${client.firstName} ${client.lastName}`}
                        {client.cuit && ` - ${formatCUIT(client.cuit)}`}
                      </option>
                    ))}
                  </select>
                  {errors.clientId?.message && typeof errors.clientId.message === 'string' && (
                    <p className="text-red-500 text-sm mt-1">{errors.clientId.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Factura</label>
                  <select
                    {...register('type')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="A">Factura A (Con CUIT)</option>
                    <option value="B">Factura B (Sin CUIT)</option>
                    <option value="C">Factura C (Consumidor Final)</option>
                  </select>
                  {watchType === 'A' && (
                    <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-1">
                      Requiere CUIT válido del cliente
                    </p>
                  )}
                </div>
              </div>

              {/* Invoice Items */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">Items de la Factura</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Agregar Item
                  </button>
                </div>
                
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-3 items-end">
                      <div className="col-span-5">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
                        <ProductCombobox
                          products={products}
                          selectedProduct={products.find((product) => product.id === item.productId) || null}
                          onCreateRequested={(query) => {
                            const params = new URLSearchParams();
                            params.set('returnTo', `${location.pathname}${location.search || ''}`);
                            if (query?.trim()) params.set('name', query.trim());
                            navigate(`/products/new?${params.toString()}`);
                          }}
                          onSelect={(product) => handleSelectProduct(index, product)}
                          placeholder={productsLoading ? 'Cargando productos...' : 'Buscar producto o servicio...'}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cantidad</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Precio Unit.</label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total</label>
                        <input
                          type="number"
                          value={item.total.toFixed(2)}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800 dark:hover:text-red-400 p-2"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {items.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded">
                    <div className="text-right space-y-1">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        Importe Total: ${calculateTotals(items).total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        IVA contenido (21%): ${calculateTotals(items).tax.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observaciones</label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Observaciones adicionales..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    reset();
                    setItems([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-black/90 dark:hover:bg-white/90 transition-colors">
                  Crear Factura
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create from Remito Modal */}
      {showFromRemitoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Crear Factura desde Remito</h3>
              <button
                onClick={() => setShowFromRemitoModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Seleccionar Remito</label>
                <select
                  value={selectedRemito}
                  onChange={(e) => setSelectedRemito(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Seleccionar...</option>
                  {remitosForInvoice.map(remito => (
                    <option key={remito.id} value={remito.id}>
                      {remito.remitNumber} - {remito.clientName} - ${remito.estimatedValue?.toFixed(2) || '0.00'}
                    </option>
                  ))}
                </select>
              </div>

              {remitosForInvoice.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No hay remitos emitidos sin facturar disponibles.
                </p>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowFromRemitoModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={createFromRemito}
                  disabled={!selectedRemito}
                  className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-black/90 dark:hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Crear Factura
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Detalle de Factura</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Invoice Header */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Información de la Factura</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Número: {selectedInvoice.invoiceNumber}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Tipo: Factura {selectedInvoice.type}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Fecha: {new Date(selectedInvoice.issueDate).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Estado: <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedInvoice.status)}`}>
                      {getStatusText(selectedInvoice.status)}
                    </span>
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Cliente</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{selectedInvoice.clientName}</p>
                  {selectedInvoice.clientDocument && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {selectedInvoice.type === 'A' ? 'CUIT' : 'Documento'}: {formatCUIT(selectedInvoice.clientDocument)}
                    </p>
                  )}
                  {selectedInvoice.clientAddress && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{selectedInvoice.clientAddress}</p>
                  )}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Items</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Producto/Servicio</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cant.</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">U. Medida</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Prec. Unit.</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{item.description}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">unidades</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">${item.unitPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">${(item.quantity * item.unitPrice).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                      <span>Subtotal:</span>
                      <span>${selectedInvoice.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                      <span>Importe Otros Tributos:</span>
                      <span>$0,00</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 dark:border-gray-600 pt-2 text-gray-900 dark:text-white">
                      <span>Importe Total:</span>
                      <span>${selectedInvoice.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {selectedInvoice.type === 'B' && selectedInvoice.taxAmount > 0 && (
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
                        <span>IVA contenido ({selectedInvoice.taxRate}%):</span>
                        <span>${selectedInvoice.taxAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedInvoice.notes && (
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Observaciones</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-3 rounded">
                    {selectedInvoice.notes}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              {selectedInvoice.status === 'authorized' && (
                <div className="flex justify-end gap-3 border-t border-gray-200 dark:border-gray-600 pt-4">
                  <button
                    onClick={() => handleDownloadPdf(selectedInvoice)}
                    className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-black/90 dark:hover:bg-white/90 transition-colors flex items-center"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Descargar PDF
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      printInvoice(selectedInvoice);
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center"
                  >
                    <PrinterIcon className="h-5 w-5 mr-2" />
                    Imprimir
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print View Modal */}
      {showPrintView && selectedInvoice && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="min-h-screen">
            <div className="flex justify-end p-4 print-hidden">
              <button
                onClick={() => setShowPrintView(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 mr-2"
              >
                Cerrar
              </button>
            </div>
            <InvoicePrintView invoice={selectedInvoice} />
          </div>
        </div>
      )}

      {/* Modal multi-remito */}
      <MultiRemitoInvoiceDialog
        open={showMultiRemitoDialog}
        onOpenChange={setShowMultiRemitoDialog}
        onSuccess={() => fetchInvoices()}
      />
    </div>
  );
};

export default Invoices; 

